import { Observable, combineLatest, distinctUntilChanged, map, shareReplay, take } from 'rxjs';

import { isEqual } from 'lodash-es';

import {
    ApiQuery,
    EmptyStructuredQuery,
    FacetProfile,
    FieldDefinition,
    QueryPartData,
    ShFacet,
    ShSelectedFacetValue,
    ShUrlQueryService
} from '@stylehaven/components';

import { ActiveFilterStateService } from '../services/active-filter-state.service';
import { ColumnEnumFilter, groupFacetsByField, toEnumFacetFieldKeys } from './column-enum-facet.helper';

/** What a host must hand over: the two streams only it knows, and where the panel's own state comes from. */
export interface ColumnHeaderFilterSources {
    /** Must replay: a header dropdown reads it on the click that opens it, long after it first emitted. */
    readonly facetProfile$: Observable<FacetProfile>;
    /** Must replay, for the same reason. */
    readonly fieldDefinitions$: Observable<FieldDefinition[]>;
    /** The facetgroepen the panel currently shows. `null` until it has said. */
    readonly panelVisibleFacetKeys$: Observable<readonly string[] | null>;
}

/**
 * Everything the column headers of one overview need to offer a filter, and everything they write back.
 *
 * The four `on…` members are the grid table's outputs, one for one.
 */
export interface ColumnHeaderFilters {
    /** The `cf` state, as query parts and as the query that narrows the list. */
    readonly columnFilters$: Observable<QueryPartData[]>;
    readonly columnFilterQuery$: Observable<ApiQuery>;
    readonly activeFacetFilters$: Observable<QueryPartData[]>;
    readonly enumFacetFieldKeys$: Observable<string[]>;
    readonly enumFacets$: Observable<ReadonlyMap<string, ShFacet>>;

    /** Sets one column's filter. An empty value removes it — which is also what its pill does. */
    setColumnFilter(field: string, value: string): void;
    openEnumFilter(dottedKey: string): void;
    clearEnumFilter(dottedKey: string): void;
    toggleFacetValue(facetValue: ShSelectedFacetValue): void;
}

/**
 * Binds one overview's column headers to the shared filter state.
 *
 * A plain factory rather than a service, following {@link columnEnumFilterFor} and `facetOrderForView`: it is
 * created per container, over that container's own facet profile and field definitions, while everything
 * above those is the same on every overview that renders the grid table.
 *
 * The `cf` state is read through {@link ActiveFilterStateService}, which is also what writes it back and what
 * the pill bar is built from — two readers of one URL parameter drift.
 *
 * A column gets an enum dropdown only where the filter panel's profile lists its field and the panel is
 * showing it, so the two are one filter with two front doors (HFBD-5).
 */
export function columnHeaderFiltersFor(
    activeFilterState: ActiveFilterStateService,
    urlQueryService: ShUrlQueryService,
    enumFilter: ColumnEnumFilter,
    sources: ColumnHeaderFilterSources
): ColumnHeaderFilters {
    const columnFilters$: Observable<QueryPartData[]> = activeFilterState.getColumnFilters().pipe(shareReplay(1));

    const activeFacetFilters$: Observable<QueryPartData[]> = urlQueryService.getSelectedFacets().pipe(
        map(selectedFacets => groupFacetsByField(selectedFacets)),
        distinctUntilChanged(isEqual),
        shareReplay(1)
    );

    const facetApiQuery$: Observable<ApiQuery> = urlQueryService
        .getStructuredQuery()
        .pipe(map(structuredQuery => new ApiQuery(structuredQuery)));

    return {
        columnFilters$,
        activeFacetFilters$,

        columnFilterQuery$: columnFilters$.pipe(
            map(queryParts => new ApiQuery({ ...EmptyStructuredQuery, ColumnFilters: queryParts })),
            shareReplay(1)
        ),

        enumFacetFieldKeys$: combineLatest([
            sources.facetProfile$.pipe(map(profile => (profile.Fields ?? []).map(field => field.DottedKey))),
            sources.panelVisibleFacetKeys$,
            activeFacetFilters$
        ]).pipe(
            map(([profileKeys, visibleKeys, activeFilters]) =>
                toEnumFacetFieldKeys(profileKeys, visibleKeys, activeFilters)
            ),
            distinctUntilChanged(isEqual)
        ),

        enumFacets$: enumFilter.facets$,

        setColumnFilter: (field: string, value: string) => activeFilterState.setColumnFilter(field, value),

        // Reads its three sources once per click rather than holding them, so the factory owns no
        // subscription that could outlive its host.
        openEnumFilter: (dottedKey: string) =>
            combineLatest([facetApiQuery$, sources.facetProfile$, sources.fieldDefinitions$])
                .pipe(take(1))
                .subscribe(([query, facetProfile, fieldDefinitions]) =>
                    enumFilter.load(dottedKey, { query, facetProfile, fieldDefinitions })
                ),

        clearEnumFilter: (dottedKey: string) => enumFilter.clear(dottedKey),
        toggleFacetValue: (facetValue: ShSelectedFacetValue) => enumFilter.toggleValue(facetValue)
    };
}
